import { defineStore } from "pinia";
import { useStorage, useDebounceFn } from "@vueuse/core";

import { useCompanyStore } from "./company";
import { useAuthStore } from "./auth";

// GraphQL
import { provideApolloClient, useMutation } from "@vue/apollo-composable";
import apolloClient from "../graphql/client";
import gql from "graphql-tag";

provideApolloClient(apolloClient);

let reloadOnboardingOverviewDebounced = null;

/** Base GraphQL where for onboarding overview (codeless, not archived). */
function buildOnboardingBaseWhere() {
  // `code` is a non-nullable String with default "" — do not filter equals: null
  // (Prisma rejects it and the query fails, returning no rows).
  return {
    AND: [
      { isArchived: { equals: false } },
      { code: { equals: "" } },
    ],
  };
}

const { mutate: createCompanyProfileRequest } = useMutation(
  gql`
    mutation CreateCompanyEmployee($data: CompanyEmployeeCreateInput!) {
      createCompanyEmployee(data: $data) {
        id
        name
      }
    }
  `
);

const { mutate: submitEmployeeForReviewRequest } = useMutation(
  gql`
    mutation SubmitEmployeeForReview($id: ID!) {
      updateCompanyEmployee(
        where: { id: $id }
        data: { 
          status: "profileInReview"
        }
      ) {
        id
      }
    }
  `
);

const { mutate: updateEmployeeStatusRequest } = useMutation(
  gql`
    mutation UpdateEmployeeStatus($id: ID!, $status: String!, $contractSigned: Boolean!) {
      updateCompanyEmployee(
        where: { id: $id }
        data: { 
          status: $status
          hasSignedContract: $contractSigned
        }
      ) {
        id
        status
      }
    }
  `
);

const { mutate: updateEmployeeStatusRequestSimple } = useMutation(
  gql`
    mutation UpdateEmployeeStatusSimple($id: ID!, $status: String!) {
      updateCompanyEmployee(
        where: { id: $id }
        data: { 
          status: $status
        }
      ) {
        id
        status
      }
    }
  `
);

const { mutate: updateEmployeeStatusRequestFromCompany } = useMutation(
  gql`
    mutation UpdateEmployeeStatusFromCompany($id: ID!, $status: String!) {
      updateCompanyEmployee(
        where: { id: $id }
        data: { 
          status: $status
        }
      ) {
        id
        status
      }
    }
  `
);

const { mutate: generateContractRequest } = useMutation(
  gql`
    mutation GenerateContract($id: ID!, $contractData: String) {
      updateCompanyEmployee(
        where: { id: $id }
        data: { 
          status: "contract-awaiting-signature",
          contractGeneratedAt: "now"
        }
      ) {
        id
        status
        contractGeneratedAt
      }
    }
  `
);

const { mutate: finalizeEmployeeRequest } = useMutation(
  gql`
    mutation FinalizeEmployee($id: ID!) {
      updateCompanyEmployee(
        where: { id: $id }
        data: { 
          status: "finalized",
          finalizedAt: "now"
        }
      ) {
        id
        status
        finalizedAt
      }
    }
  `
);

const { mutate: assignIdAndFinalizeRequest } = useMutation(
  gql`
    mutation AssignIdAndFinalize($id: ID!, $code: String!) {
      updateCompanyEmployee(
        where: { id: $id }
        data: { 
          code: $code,
          status: "finalized"
        }
      ) {
        id
        code
        status
        finalizedAt
      }
    }
  `
);

const { mutate: requestCancelOnboardingRequest } = useMutation(
  gql`
    mutation RequestCancelOnboarding($id: ID!) {
      updateCompanyEmployee(
        where: { id: $id }
        data: { 
          status: "cancelRequested"
        }
      ) {
        id
        status
        previousStatus
      }
    }
  `
);

const { mutate: deleteCompanyEmployeeRequest } = useMutation(
  gql`
    mutation DeleteCompanyEmployee($id: ID!) {
      deleteCompanyEmployee(where: { id: $id }) {
        id
      }
    }
  `
);

export const useEmployeeStore = defineStore("employees", {
  state: () => ({
    allEmployees: useStorage("employeesAllEmployees", []),
    selected: useStorage("employeesSelected", {}),
    lastSelected: {},
    loading: false,
    lastChange: useStorage("employeesLastChange", {}),
    filterByList: useStorage("employeesFilterByList", "normal"), // normal (with code archived=false) or archived (archived=true) or new (archived=false, no code)
    filterByName: useStorage("employeesFilterByName", ""),
    // Admin onboarding overview (filtered server-side)
    onboardingOverviewEmployees: [],
    onboardingOverviewTotal: 0,
    onboardingCompanyId: null,
    onboardingStatus: null,
    onboardingMyCompaniesOnly: true,
  }),
  getters: {
    employees: (state) => {
      return state.allEmployees.filter((e) => e.company?.id === useCompanyStore().selectedCompanyId) || [];
    },
    companyEmployees: (state) => {
      return (
        state.allEmployees.filter(
          (e) => e.company?.id === useCompanyStore().selectedCompanyId && e.isHiddenInHr === false
        ) || []
      );
    },
    normalListEmployees: (state) => {
      return state.companyEmployees.filter((e) => e.isArchived === false && e.code !== "");
    },
    archivedListEmployees: (state) => {
      return state.companyEmployees.filter((e) => e.isArchived === true);
    },
    newListEmployees: (state) => {
      return state.companyEmployees.filter((e) => e.code === "" && e.isArchived === false);
    },
    filteredEmployees: (state) => {
      // Filter by list
      let allCompanyEmployeesByList;
      if (state.filterByList === "archived") {
        allCompanyEmployeesByList = state.archivedListEmployees;
      }
      if (state.filterByList === "new") {
        allCompanyEmployeesByList = state.newListEmployees;
      }
      if (state.filterByList === "normal") {
        allCompanyEmployeesByList = state.normalListEmployees;
      }

      // Filter by name
      let allCompanyEmployeesByListAndName;
      if (state.filterByName !== "") {
        allCompanyEmployeesByListAndName = allCompanyEmployeesByList.filter((e) =>
          e.name.toLowerCase().includes(state.filterByName.toLowerCase())
        );
      } else {
        allCompanyEmployeesByListAndName = allCompanyEmployeesByList;
      }
      return allCompanyEmployeesByListAndName;
    },
    /* Used by UI */
    employeesByLetter: (state) => {
      return (letter) => state.filteredEmployees.filter((e) => e.letter === letter);
    },
    /* Used by UI */
    employeesLetters() {
      if (this.filteredEmployees.length > 0) {
        return [...new Set(this.filteredEmployees.map((employee) => employee.letter).sort())];
      }
      return [];
    },
    /*
    isSelectedAvailable(state) {
      if (this.employees.filter((employee) => employee.id === state.selected.id).length > 0) {
        return true;
      }
      return false;
    },
    */
    selectedEmployee: (state) => {
      if (useAuthStore().account?.role?.canAccessAllCompanies) {
        return state.lastSelected;
      }
      if (state.allEmployees.length === 1) {
        return state.allEmployees[0];
      }
      if (state.allEmployees.length > 1) {
        return state.selected[useAuthStore().selectedCompany] ?? undefined;
      }
      return {};
    },

    /**
     * Pass in employee code and company code and return employee id
     * @param {code, companycode} state
     * @returns id of employee
     */
    employeeUsingCodes: (state) => (code, companycode) => {
      const employee = state.allEmployees.find((e) => e.code === code && e.company.code === companycode);
      return employee ? employee : null;
    },
    allEmployeesReturnEmployeeUsingId: (state) => (userid) => {
      let employee = null;

      const find = state.allEmployees.find((e) => e.id === userid);
      if (find) {
        employee = find;
      }

      return employee;
    },
    /**
     * Onboarding overview employees — already filtered server-side via GraphQL where.
     */
    onboardingEmployees: (state) => state.onboardingOverviewEmployees,
  },
  actions: {
    scheduleReloadOnboardingOverview() {
      if (!reloadOnboardingOverviewDebounced) {
        reloadOnboardingOverviewDebounced = useDebounceFn(() => {
          this.getOnboardingOverviewEmployees();
        }, 300);
      }
      reloadOnboardingOverviewDebounced();
    },
    /**
     * Load onboarding overview employees with company/status filters applied in the query.
     * Does not touch allEmployees (shared by the rest of HR).
     */
    async getOnboardingOverviewEmployees() {
      this.loading = true;

      try {
        const baseWhere = buildOnboardingBaseWhere();
        const and = [...baseWhere.AND];

        if (this.onboardingMyCompaniesOnly) {
          const companyStore = useCompanyStore();
          await companyStore.getData(true);
          const userId = useAuthStore().loginAccount?.id;
          const myCompanyIds = userId
            ? companyStore.allCompanies
                .filter(
                  (item) =>
                    item.personInChargeAccounting?.id === userId ||
                    item.personInChargeHumanResources?.id === userId
                )
                .map((item) => item.id)
            : [];

          if (this.onboardingCompanyId) {
            and.push({
              company: {
                id: {
                  in: myCompanyIds.includes(this.onboardingCompanyId)
                    ? [this.onboardingCompanyId]
                    : [],
                },
              },
            });
          } else {
            and.push({ company: { id: { in: myCompanyIds } } });
          }
        } else if (this.onboardingCompanyId) {
          and.push({ company: { id: { equals: this.onboardingCompanyId } } });
        }

        if (this.onboardingStatus) {
          and.push({ status: { equals: this.onboardingStatus } });
        }

        const where = { AND: and };

        const result = await apolloClient.query({
          query: gql`
            query OnboardingOverviewEmployees(
              $where: CompanyEmployeeWhereInput!
              $baseWhere: CompanyEmployeeWhereInput!
              $orderBy: [CompanyEmployeeOrderByInput!]!
            ) {
              companyEmployees(orderBy: $orderBy, where: $where) {
                id
                code
                name
                company {
                  id
                  code
                  name
                }
                isArchived
                status
                previousStatus
                profileSubmittedAt
              }
              onboardingTotal: companyEmployeesCount(where: $baseWhere)
            }
          `,
          variables: {
            where,
            baseWhere,
            orderBy: [{ profileSubmittedAt: "desc" }],
          },
          fetchPolicy: "no-cache",
        });

        if (result.data?.companyEmployees) {
          this.onboardingOverviewEmployees = result.data.companyEmployees;
          this.onboardingOverviewTotal = result.data.onboardingTotal ?? 0;
          this.loading = false;
          return { code: "SUCCESS", message: "Load complete" };
        }

        this.onboardingOverviewEmployees = [];
        this.onboardingOverviewTotal = 0;
        this.loading = false;
        return { code: "ERROR", message: "Error while loading onboarding employees" };
      } catch (e) {
        console.error("getOnboardingOverviewEmployees failed", e);
        this.onboardingOverviewEmployees = [];
        this.onboardingOverviewTotal = 0;
        this.loading = false;
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async getData() {
      try {
        if (this.loading) return;
        this.loading = true;

        //const searchStore = useSearchStore();
        //console.dir(searchStore.getHrCompanyFilter);

        const variablesWhere = {};

        // use apollo client directly
        const result = await apolloClient.query({
          query: gql`
            query Query($orderBy: [CompanyEmployeeOrderByInput!]!, $where: CompanyEmployeeWhereInput!) {
              companyEmployees(orderBy: $orderBy, where: $where) {
                id
                code
                name
                jobDescription
                hrDocumentNotification
                hrUnReadChatMessages
                letter
                settingWantsSalaryWorkflowNotification
                settingWantsAccountingNotification
                settingWantsHRSalariesNotification
                settingHrDocumentsDisabled
                user {
                  id
                  emailHash
                }
                company {
                  id
                  code
                  name
                }
                isHiddenInHr
                isArchived
                status
                previousStatus
                hasSignedContract
                profileSubmittedAt
                contractGeneratedAt
                contractSignedAt
                finalizedAt
              }
            }
          `,
          variables: {
            orderBy: {
              name: "asc",
            },
            where: variablesWhere,
          },
          fetchPolicy: "no-cache",
        });
        //console.dir(result);
        if (result.data?.companyEmployees) {
          if (result.data.companyEmployees.length > 0) {
            this.allEmployees = result.data.companyEmployees;
            this.autoSelectFirstEmployeeUsingLetters();
            this.loading = false;
            return { code: "SUCCESS", message: "Load complete" };
          }
          this.documents = [];
          this.loading = false;
          return { code: "SUCCESS", message: "No employees found" };
        }
        //console.dir(result);
        this.loading = false;
        return { code: "ERROR", message: "Error while loading employees" };
      } catch (e) {
        //console.dir(e);
        this.loading = false;
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async createCompanyEmployee(name) {
      try {
        const result = await createCompanyProfileRequest({
          data: {
            name,
            company: { connect: { id: useCompanyStore().selectedCompanyId } },
          },
        });
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        if (result.data.createCompanyEmployee.error) {
          return {
            code: "ERROR",
            message: result.data.createCompanyEmployee.message,
          };
        }
        return result;
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async submitForReview(employeeId) {
      try {
        const result = await submitEmployeeForReviewRequest({
          id: employeeId,
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex] = {
            ...this.allEmployees[employeeIndex],
            status: "profileInReview"
          };
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected = {
            ...this.lastSelected,
            status: "profileInReview"
          };
        }
        
        return { code: "SUCCESS", message: "Employee submitted for review successfully" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async updateEmployeeStatus(employeeId, status, contractSigned = false) {
      try {
        const result = await updateEmployeeStatusRequest({
          id: employeeId,
          status,
          contractSigned
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex].status = status;
          this.allEmployees[employeeIndex].hasSignedContract = contractSigned;
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected.status = status;
          this.lastSelected.hasSignedContract = contractSigned;
        }
        
        return { code: "SUCCESS", message: "Employee status updated successfully" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async updateEmployeeStatusSimple(employeeId, status) {
      try {
        const result = await updateEmployeeStatusRequestSimple({
          id: employeeId,
          status
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex].status = status;
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected.status = status;
        }
        
        return { code: "SUCCESS", message: "Employee status updated successfully" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async proceedToContractGeneration(employeeId) {
      try {
        const result = await updateEmployeeStatusRequest({
          id: employeeId,
          status: "contractGeneration",
          contractSigned: false
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex] = {
            ...this.allEmployees[employeeIndex],
            status: "contractGeneration",
            hasSignedContract: false
          };
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected = {
            ...this.lastSelected,
            status: "contractGeneration",
            hasSignedContract: false
          };
        }
        
        return { code: "SUCCESS", message: "Employee moved to contract generation phase" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async generateContract(employeeId, contractData = null) {
      try {
        const result = await generateContractRequest({
          id: employeeId,
          contractData: contractData
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex] = {
            ...this.allEmployees[employeeIndex],
            status: "contractAwaitingSignature",
            contractGeneratedAt: new Date().toISOString()
          };
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected = {
            ...this.lastSelected,
            status: "contractAwaitingSignature",
            contractGeneratedAt: new Date().toISOString()
          };
        }
        
        return { code: "SUCCESS", message: "Contract generated and awaiting signature" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async markContractInReview(employeeId) {
      try {
        const result = await updateEmployeeStatusRequestFromCompany({
          id: employeeId,
          status: "contractInReview"
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex] = {
            ...this.allEmployees[employeeIndex],
            status: "contractInReview",
            hasSignedContract: true
          };
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected = {
            ...this.lastSelected,
            status: "contractInReview",
            hasSignedContract: true
          };
        }
        
        return { code: "SUCCESS", message: "Contract marked as in review" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async finalizeEmployee(employeeId) {
      try {
        const result = await finalizeEmployeeRequest({
          id: employeeId,
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex] = {
            ...this.allEmployees[employeeIndex],
            status: "finalized",
            finalizedAt: new Date().toISOString()
          };
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected = {
            ...this.lastSelected,
            status: "finalized",
            finalizedAt: new Date().toISOString()
          };
        }
        
        return { code: "SUCCESS", message: "Employee onboarding finalized successfully" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async requestCancelOnboarding(employeeId) {
      try {
        const employee = this.allEmployees.find(e => e.id === employeeId) || this.lastSelected
        const currentStatus = employee?.status || 'created'
        
        const result = await requestCancelOnboardingRequest({
          id: employeeId
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex] = {
            ...this.allEmployees[employeeIndex],
            status: "cancelRequested"
          };
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected = {
            ...this.lastSelected,
            status: "cancelRequested"
          };
        }
        
        return { code: "SUCCESS", message: "Cancel request submitted successfully" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async deleteEmployee(employeeId) {
      try {
        const result = await deleteCompanyEmployeeRequest({
          id: employeeId
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Remove from local state
        this.allEmployees = this.allEmployees.filter(e => e.id !== employeeId);
        
        // Clear lastSelected if it's the deleted employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected = {};
        }
        
        // Clear selected if it's the deleted employee
        if (this.selected?.id === employeeId) {
          this.selected = {};
        }
        
        return { code: "SUCCESS", message: "Employee profile deleted successfully" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async assignIdAndFinalize(employeeId, code) {
      try {
        const result = await assignIdAndFinalizeRequest({
          id: employeeId,
          code: code
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex] = {
            ...this.allEmployees[employeeIndex],
            code: code,
            status: "finalized",
            finalizedAt: new Date().toISOString()
          };
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected = {
            ...this.lastSelected,
            code: code,
            status: "finalized",
            finalizedAt: new Date().toISOString()
          };
        }
        
        return { code: "SUCCESS", message: "Employee ID assigned and onboarding finalized successfully" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async rejectToCreated(employeeId) {
      try {
        const result = await updateEmployeeStatusRequest({
          id: employeeId,
          status: "created"
        });
        
        if (result.errors) {
          return { code: "ERROR", message: result.errors[0].message };
        }
        
        // Update local state
        const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
        if (employeeIndex !== -1) {
          this.allEmployees[employeeIndex] = {
            ...this.allEmployees[employeeIndex],
            status: "created"
          };
        }
        
        // Update lastSelected if it's the same employee
        if (this.lastSelected?.id === employeeId) {
          this.lastSelected = {
            ...this.lastSelected,
            status: "created"
          };
        }
        
        return { code: "SUCCESS", message: "Employee returned to created status for corrections" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    async reloadEmployeeStatus(employeeId) {
      try {
        if (!employeeId) {
          return { code: "ERROR", message: "Employee ID is required" };
        }

        // Query only employee status and related onboarding fields
        const result = await apolloClient.query({
          query: gql`
            query GetEmployeeStatus($employeeId: ID!) {
              companyEmployee(where: { id: $employeeId }) {
                id
                status
                previousStatus
                hasSignedContract
                profileSubmittedAt
                contractGeneratedAt
                contractSignedAt
                finalizedAt
              }
            }
          `,
          variables: { employeeId },
          fetchPolicy: "no-cache",
        });

        if (result.data?.companyEmployee) {
          const statusData = result.data.companyEmployee;
          
          // Update employee status in allEmployees array
          const employeeIndex = this.allEmployees.findIndex(e => e.id === employeeId);
          if (employeeIndex !== -1) {
            this.allEmployees[employeeIndex] = {
              ...this.allEmployees[employeeIndex],
              status: statusData.status,
              previousStatus: statusData.previousStatus,
              hasSignedContract: statusData.hasSignedContract,
              profileSubmittedAt: statusData.profileSubmittedAt,
              contractGeneratedAt: statusData.contractGeneratedAt,
              contractSignedAt: statusData.contractSignedAt,
              finalizedAt: statusData.finalizedAt
            };
          }
          
          // Update selected employee if it's the same one
          if (this.lastSelected?.id === employeeId) {
            this.lastSelected = {
              ...this.lastSelected,
              status: statusData.status,
              previousStatus: statusData.previousStatus,
              hasSignedContract: statusData.hasSignedContract,
              profileSubmittedAt: statusData.profileSubmittedAt,
              contractGeneratedAt: statusData.contractGeneratedAt,
              contractSignedAt: statusData.contractSignedAt,
              finalizedAt: statusData.finalizedAt
            };
          }
          
          // Update selected employee in company context if it's the same one
          const companyId = this.lastSelected?.company?.id;
          if (companyId && this.selected[companyId]?.id === employeeId) {
            this.selected[companyId] = {
              ...this.selected[companyId],
              status: statusData.status,
              previousStatus: statusData.previousStatus,
              hasSignedContract: statusData.hasSignedContract,
              profileSubmittedAt: statusData.profileSubmittedAt,
              contractGeneratedAt: statusData.contractGeneratedAt,
              contractSignedAt: statusData.contractSignedAt,
              finalizedAt: statusData.finalizedAt
            };
          }
          
          return { code: "SUCCESS", message: "Employee status reloaded successfully", status: statusData.status };
        }
        
        return { code: "ERROR", message: "Employee not found" };
      } catch (e) {
        return { code: "ERROR", message: `${e.message} (${e.code})` };
      }
    },
    resetData() {
      this.allEmployees = [];
      this.selected = {};
      this.lastSelected = {};
      this.loading = false;
      this.lastChange = {};
      this.filterByList = "normal";
      this.filterByName = "";
    },
    setEmployee(employee) {
      //if (!employee) return;
      if (employee === null) {
        this.lastSelected = {};
        return;
      }

      if (employee.id && this.lastSelected.id !== employee.id) {
        this.lastSelected = employee;
        this.selected[employee.company.id] = employee;
      }
    },
    autoSelectFirstEmployeeUsingLetters() {
      const letter = this.employeesLetters[0];
      const eByLetter = this.employeesByLetter(letter);

      if (eByLetter.length > 0 && eByLetter[0]) {
        this.setEmployee(eByLetter[0]);
      } else {
        this.setEmployee(null);
      }
    },
  },
});
